import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { MANUAL_PATHS, buildManualUrl } from '../utils/manualUrl';

// Component for user icon with fallback
const SafeUserIcon: React.FC<{ user: User; className?: string }> = ({ user, className }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const handleImageError = () => {
    setImageError(true);
  };
  
  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };
  
  const showFallback = imageError || !user.iconUrl || user.iconUrl === '/images/icon/android-chrome-192x192.png';
  const userInitial = user.name ? user.name.charAt(0).toUpperCase() : '?';
  
  return (
    <div className={className} style={{ width: '100%', height: '100%', position: 'relative' }}>
      {!showFallback && (
        <UserIcon
          src={user.iconUrl}
          alt={user.name}
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{ display: imageLoaded && !imageError ? 'block' : 'none' }}
        />
      )}
      {showFallback && (
        <UserIconFallback>
          {userInitial}
        </UserIconFallback>
      )}
    </div>
  );
};

const DropdownContainer = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  overflow: hidden;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: transform ${({ theme }) => theme.transitions.base};

  &:hover {
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const UserIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  
  &:not([src]),
  &[src=""],
  &[src="null"],
  &[src="undefined"] {
    display: none;
  }
`;

const UserIconFallback = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.75rem;
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  width: 238px;
  z-index: 1000;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: all ${({ theme }) => theme.transitions.base};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const UserInfoIcon = styled.div`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  overflow: hidden;
`;

const UserInfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const UserName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const UserEmail = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
  margin: 0.5rem 0;
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color ${({ theme }) => theme.transitions.base};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const MenuItemIcon = styled.div`
  width: 1rem;
  height: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MenuItemText = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  flex: 1;
`;

const LogoutItem = styled(MenuItem)`
  color: ${({ theme }) => theme.colors.error};
  
  ${MenuItemIcon} {
    color: ${({ theme }) => theme.colors.error};
  }
  
  ${MenuItemText} {
    color: ${({ theme }) => theme.colors.error};
  }
`;

interface UserDropdownProps {
  user: User;
  onSettingsClick: () => void;
}

const UserDropdown: React.FC<UserDropdownProps> = ({ user, onSettingsClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSettingsClick = () => {
    onSettingsClick();
    setIsOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const handleTfactClick = () => {
    window.open('https://bff.dev.sstf.agest.app', '_blank');
  };

  const handleManualClick = () => {
    window.open(buildManualUrl(MANUAL_PATHS.incidentReporterMain), '_blank');
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <UserButton onClick={() => setIsOpen(!isOpen)}>
        <SafeUserIcon user={user} />
      </UserButton>

      <DropdownMenu isOpen={isOpen}>
        <UserInfo>
          <UserInfoIcon>
            <SafeUserIcon user={user} />
          </UserInfoIcon>
          <UserInfoText>
            <UserName>{user.name}</UserName>
            <UserEmail>{user.email}</UserEmail>
          </UserInfoText>
        </UserInfo>

        <Divider />

        <MenuItem onClick={handleSettingsClick}>
          <MenuItemIcon>
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path d="m7.692 19.5-.38-3.046a5.31 5.31 0 0 1-1.579-.896L2.91 16.75l-2.307-4 2.44-1.844a5.016 5.016 0 0 1-.05-.448 7 7 0 0 1 0-.882 7.36 7.36 0 0 1 .05-.482L.604 7.25 2.91 3.27l2.812 1.182a6.4 6.4 0 0 1 .772-.524c.274-.158.542-.286.807-.382L7.692.5h4.615l.381 3.056c.3.109.571.236.814.381.243.146.489.318.736.515l2.85-1.183 2.308 3.981-2.48 1.873c.037.162.056.313.06.453.003.14.004.282.004.424a9.1 9.1 0 0 1-.01.414c-.006.14-.029.301-.069.482l2.46 1.854-2.308 4-2.815-1.202c-.247.197-.5.372-.758.524a4.58 4.58 0 0 1-.792.372l-.38 3.056H7.691Zm2.32-6.5c.831 0 1.54-.292 2.123-.876A2.893 2.893 0 0 0 13.011 10c0-.832-.292-1.54-.876-2.124A2.893 2.893 0 0 0 10.011 7c-.842 0-1.553.292-2.132.876A2.905 2.905 0 0 0 7.011 10c0 .832.29 1.54.868 2.124.58.584 1.29.876 2.132.876Z"/>
            </svg>
          </MenuItemIcon>
          <MenuItemText>プロジェクト設定</MenuItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleTfactClick}>
          <MenuItemText>
            TFACT
            <svg width="16" height="16" viewBox="0 0 20 22" fill="none" style={{ marginLeft: '0.5rem' }}>
              <path fillRule="evenodd" clipRule="evenodd" d="M17.2585 2.74408C17.584 3.06951 17.584 3.59715 17.2585 3.92259L9.75853 11.4226C9.43309 11.748 8.90545 11.748 8.58002 11.4226C8.25458 11.0972 8.25458 10.5695 8.58002 10.2441L16.08 2.74408C16.4055 2.41864 16.9331 2.41864 17.2585 2.74408Z" fill="#858585"/>
            </svg>
          </MenuItemText>
        </MenuItem>

        <MenuItem onClick={handleManualClick}>
          <MenuItemText>
            TFACT manual
            <svg width="16" height="16" viewBox="0 0 20 22" fill="none" style={{ marginLeft: '0.5rem' }}>
              <path fillRule="evenodd" clipRule="evenodd" d="M17.2585 2.74408C17.584 3.06951 17.584 3.59715 17.2585 3.92259L9.75853 11.4226C9.43309 11.748 8.90545 11.748 8.58002 11.4226C8.25458 11.0972 8.25458 10.5695 8.58002 10.2441L16.08 2.74408C16.4055 2.41864 16.9331 2.41864 17.2585 2.74408Z" fill="#858585"/>
            </svg>
          </MenuItemText>
        </MenuItem>

        <Divider />

        <LogoutItem onClick={handleLogout}>
          <MenuItemIcon>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" clipRule="evenodd" d="M7.37868 3.38389C7.54277 3.21979 7.76533 3.1276 7.9974 3.1276H12.6641C12.8961 3.1276 13.1187 3.21979 13.2828 3.38389C13.4469 3.54798 13.5391 3.77054 13.5391 4.0026V12.0026C13.5391 12.2347 13.4469 12.4572 13.2828 12.6213C13.1187 12.7854 12.8961 12.8776 12.6641 12.8776H7.9974C7.76533 12.8776 7.54277 12.7854 7.37868 12.6213C7.21458 12.4572 7.1224 12.2347 7.1224 12.0026V10.6693C7.1224 10.4161 6.91719 10.2109 6.66406 10.2109C6.41093 10.2109 6.20573 10.4161 6.20573 10.6693V12.0026C6.20573 12.4778 6.39449 12.9335 6.7305 13.2695C7.0665 13.6055 7.52222 13.7943 7.9974 13.7943H12.6641C13.1392 13.7943 13.595 13.6055 13.931 13.2695C14.267 12.9335 14.4557 12.4778 14.4557 12.0026V4.0026C14.4557 3.52742 14.267 3.07171 13.931 2.7357C13.595 2.3997 13.1392 2.21094 12.6641 2.21094H7.9974C7.52222 2.21094 7.0665 2.3997 6.7305 2.7357C6.39449 3.07171 6.20573 3.52742 6.20573 4.0026V5.33594C6.20573 5.58907 6.41093 5.79427 6.66406 5.79427C6.91719 5.79427 7.1224 5.58907 7.1224 5.33594V4.0026C7.1224 3.77054 7.21458 3.54798 7.37868 3.38389ZM4.3215 5.67851C4.50049 5.8575 4.50049 6.1477 4.3215 6.32669L3.10392 7.54427H9.99741C10.2505 7.54427 10.4557 7.74947 10.4557 8.0026C10.4557 8.25573 10.2505 8.46094 9.99741 8.46094H3.10391L4.32149 9.67851C4.50048 9.8575 4.50048 10.1477 4.32149 10.3267C4.1425 10.5057 3.8523 10.5057 3.67331 10.3267L1.67456 8.32795C1.66966 8.32309 1.66487 8.31812 1.66018 8.31303C1.5865 8.2331 1.54644 8.13334 1.53999 8.03183C1.53193 7.90499 1.57637 7.77545 1.67331 7.67851L1.67541 7.67642L3.67332 5.67851C3.85231 5.49952 4.14251 5.49952 4.3215 5.67851Z"/>
            </svg>
          </MenuItemIcon>
          <MenuItemText>ログアウトする</MenuItemText>
        </LogoutItem>
      </DropdownMenu>
    </DropdownContainer>
  );
};

export default UserDropdown;